import React from 'react';
import { Admin, defaultTheme } from 'react-admin';
import merge from 'lodash/merge';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import { authProvider, Dashboard, history } from 'cw-core-admin-ui/src/coreWalletAdminUi';
import CWResource from 'cw-core-admin-ui/src/components/wrapper/CWResource';
import {
	users,
	wallets,
	walletAccounts,
	walletAccountItems,
	paymentInstrumentDocuments,
	paymentInstruments,
	balanceTransactions,
	payments,
	invoices,
	accountingAccounts,
	accountingAccountItems,
	accountingAccountTransfers,
	paymentTransactions,
	paymentTransactionLogs,
	paymentFailures,
	paymentApprovals,
	unmatchedPayments,
	businessContracts,
	businessContractParameters,
	businessContractRoutes,
	binGroups,
	subsidiaries,
	currencies,
	vatDefinitions,
	blackWhiteLists,
	blackWhiteListItems,
	walletNotifications,
	feeGroups,
	feeDefinitions,
	limitGroups,
	limitDefinitions,
	idDocuments
} from 'cw-core-admin-ui/src/resources';

import englishMessages from './i18n/en';
import Layout from './layout/Layout';
import purchases from './resources/purchases';
import shopConfigurations from './resources/shopConfigurations';
import loyaltyPrograms from './resources/loyaltyPrograms';
import loyaltyProgramMemberships from './resources/loyaltyProgramMemberships';
import loyaltyCredits from './resources/loyaltyCredits';
import marketplaces from './resources/marketplaces';
import marketplacePurchases from './resources/marketplaces/purchases';
import invoiceVouchers from './resources/invoiceVoucher';
import marketplaceSellers from './resources/marketplaces/sellers';
import marketplaceAuthentications from './resources/marketplaces/authentications';
import dataProvider from './dataProvider';
import mergedTypes from './demoWalletTypes';
import { LinkField } from 'cw-core-admin-ui/src/components/fields';

const i18nProvider = polyglotI18nProvider((locale) => englishMessages, 'en');

// Update LinkField mapping dictionary with an enhanced one which includes DemoWallet specific types:
LinkField.defaultProps.mappingDictionary = mergedTypes;

// Improve the default theme legibility by separating table headers more clearly from table cells
const theme = merge({}, defaultTheme, {
	overrides: {
		RaDatagrid: {
			headerCell: {
				fontWeight: 'bold',
				borderBottomWidth: 2,
			},
		},
	},
});

const App = () => (
	<Admin dataProvider={dataProvider} authProvider={authProvider} i18nProvider={i18nProvider} history={history} layout={Layout} dashboard={Dashboard} theme={theme}>
		{/* CoreWallet specific resources */}
		<CWResource name="users" {...users} />
		<CWResource name="wallets" {...wallets} />
		<CWResource name="walletAccounts" {...walletAccounts} />
		<CWResource name="walletAccountItems" {...walletAccountItems} />
		<CWResource name="paymentInstrumentDocuments" {...paymentInstrumentDocuments} />
		<CWResource name="paymentInstruments" {...paymentInstruments} />
		<CWResource name="balanceTransactions" {...balanceTransactions} />
		<CWResource name="payments" {...payments} />
		<CWResource name="invoices" {...invoices} />
		<CWResource name="accountingAccounts" {...accountingAccounts} />
		<CWResource name="accountingAccountItems" {...accountingAccountItems} />
		<CWResource name="accountingAccountTransfers" {...accountingAccountTransfers} />
		<CWResource name="paymentTransactions" {...paymentTransactions} />
		<CWResource name="paymentTransactionLogs" {...paymentTransactionLogs} />
		<CWResource name="paymentFailures" {...paymentFailures} />
		<CWResource name="paymentApprovals" {...paymentApprovals} />
		<CWResource name="unmatchedPayments" {...unmatchedPayments} />
		<CWResource name="businessContracts" {...businessContracts} />
		<CWResource name="businessContractParameters" {...businessContractParameters} />
		<CWResource name="businessContractRoutes" {...businessContractRoutes} />
		<CWResource name="binGroups" {...binGroups} />
		<CWResource name="subsidiaries" {...subsidiaries} />
		<CWResource name="currencies" {...currencies} />
		<CWResource name="vatDefinitions" {...vatDefinitions} />
		<CWResource name="blackWhiteLists" {...blackWhiteLists} />
		<CWResource name="blackWhiteListItems" {...blackWhiteListItems} />
		<CWResource name="walletNotifications" {...walletNotifications} />
		<CWResource name="feeGroups" {...feeGroups} />
		<CWResource name="feeDefinitions" {...feeDefinitions} />
		<CWResource name="limitGroups" {...limitGroups} />
		<CWResource name="limitDefinitions" {...limitDefinitions} />
		<CWResource name="idDocuments" {...idDocuments} />

		{/* DemoWallet specific resources */}
		<CWResource name="purchases" {...purchases} />
		<CWResource name="shopConfigurations" {...shopConfigurations} />
		<CWResource name="loyaltyPrograms" {...loyaltyPrograms} />
		<CWResource name="loyaltyProgramMemberships" {...loyaltyProgramMemberships} />
		<CWResource name="loyaltyCredits" {...loyaltyCredits} />
		<CWResource name="marketplaces" {...marketplaces} />
		<CWResource name="marketplacePurchases" {...marketplacePurchases} />
		<CWResource name="invoiceVouchers" {...invoiceVouchers} />
		<CWResource name="marketplaceSellers" {...marketplaceSellers} />
		<CWResource name="marketplaceAuthentications" {...marketplaceAuthentications} />
	</Admin>
);

export default App;
